/**
 * SidebarAkteTab - Versicherten file (Akte) tab content for VideoSidebar
 * Displays real Versicherten and appointment data from the current session
 */

import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@jourfx/ui";
import {
  User,
  Phone,
  Mail,
  CreditCard,
  Cake,
  Calendar,
  Clock,
  Tag,
} from "lucide-react";
import type { Room } from "livekit-client";
import { VideoDebugStats } from "./VideoDebugStats";

// Type-only alias for Convex Id type
type Id<T extends string> = string & { __tableName: T };

interface SidebarAkteTabProps {
  appointmentId: string;
  /** Demo mode skips Convex queries and uses mock data */
  isDemo?: boolean;
  /** LiveKit room instance for debug stats */
  room?: Room | null;
}

function formatDateOfBirth(isoDate: string): string {
  try {
    const [year, month, day] = isoDate.split("-");
    return `${day}.${month}.${year}`;
  } catch {
    return isoDate;
  }
}

function formatDateTime(timestamp: number, timezone: string): string {
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    }).format(new Date(timestamp));
  } catch {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  }
}

const statusMap: Record<string, { label: string; className: string }> = {
  scheduled: { label: "Geplant", className: "bg-blue-500/20 text-blue-400" },
  confirmed: { label: "Bestätigt", className: "bg-green-500/20 text-green-400" },
  in_progress: { label: "Laufend", className: "bg-amber-500/20 text-amber-400" },
  completed: { label: "Abgeschlossen", className: "bg-muted text-muted-foreground" },
  cancelled: { label: "Storniert", className: "bg-destructive/20 text-destructive" },
  no_show: { label: "Nicht erschienen", className: "bg-destructive/20 text-destructive" },
};

function InfoRow({
  icon: Icon,
  label,
  value,
  mono,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | undefined | null;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-1.5">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className={`text-sm text-foreground ${mono ? "font-mono" : ""}`}>
          {value}
        </dd>
      </div>
    </div>
  );
}

export function SidebarAkteTab({ appointmentId, isDemo = false, room }: SidebarAkteTabProps) {
  const { t } = useTranslation("video");
  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);
  const isDebug = testingMode?.enabled ?? false;

  const appointment = useQuery(
    api.appointments.index.get,
    isDemo ? "skip" : { appointmentId: appointmentId as Id<"appointments"> }
  );

  if (!appointment && !isDemo) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        {t("file.loading")}
      </div>
    );
  }

  const anrede = appointment?.versicherterAnrede;
  const fullName = anrede
    ? `${anrede} ${appointment?.versicherterName}`
    : appointment?.versicherterName;

  const status = statusMap[appointment?.status ?? ""] ?? null;

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {/* Versicherten Info */}
          <div className="rounded-lg bg-muted p-4">
            <h4 className="font-medium text-foreground mb-3">
              {t("file.versichertenData")}
            </h4>
            <dl className="space-y-0.5">
              <InfoRow
                icon={User}
                label={t("file.name")}
                value={fullName}
              />
              <InfoRow
                icon={Cake}
                label={t("file.dateOfBirth")}
                value={
                  appointment?.versichertenProfile?.dateOfBirth
                    ? formatDateOfBirth(appointment.versichertenProfile.dateOfBirth)
                    : undefined
                }
              />
              <InfoRow
                icon={CreditCard}
                label={t("file.insuranceNumber")}
                value={appointment?.versicherterVersichertennummer}
                mono
              />
              <InfoRow
                icon={Mail}
                label={t("file.email")}
                value={appointment?.versicherterEmail}
              />
              <InfoRow
                icon={Phone}
                label={t("file.phone")}
                value={appointment?.versicherterPhone}
              />
            </dl>
          </div>

          {/* Appointment Details */}
          <div className="rounded-lg bg-muted p-4">
            <h4 className="font-medium text-foreground mb-3">
              {t("file.appointmentDetails")}
            </h4>
            <dl className="space-y-0.5">
              <InfoRow
                icon={Calendar}
                label={t("file.scheduledAt")}
                value={
                  appointment?.scheduledAt
                    ? formatDateTime(appointment.scheduledAt, appointment.timezone ?? "Europe/Berlin")
                    : undefined
                }
              />
              <InfoRow
                icon={Clock}
                label={t("file.duration")}
                value={
                  appointment?.duration
                    ? `${appointment.duration} ${t("file.minutes")}`
                    : undefined
                }
              />
              {appointment?.templateName && (
                <InfoRow
                  icon={Tag}
                  label={t("file.template")}
                  value={appointment.templateName}
                />
              )}
            </dl>
            {status && (
              <div className="mt-3">
                <span
                  className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${status.className}`}
                >
                  {status.label}
                </span>
              </div>
            )}
          </div>

          {/* Debug: Video quality stats (testing mode only) */}
          {isDebug && (
            <VideoDebugStats room={room ?? null} />
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
